import React, { useCallback, useRef, useState } from 'react';
import { DropdownMenuItem, StudyList } from '@ohif/ui-next';
import { useSystem } from '@ohif/core';
import { deleteStudy } from '../../clients/viewer';
import { describeError } from '../../utils';
import DeleteConfirmDialog from './DeleteConfirmDialog';

interface DeleteActionProps {
  // `any` on both: these are the TanStack cell-context members the customization
  // hands through. Only `row.original` and the four `table` methods below are
  // used, and typing against @tanstack/react-table's full generic Cell<T>/Table<T>
  // buys nothing those call sites don't already pin down.
  cell: any;
  table: any;
}

/**
 * Plan D-1. Clamped BEFORE the refresh fires, while THIS row's own cell is
 * still mounted: `table.getRowModel()` still reflects the PRE-delete row
 * count, which is the only moment a cell instance is guaranteed to exist for
 * the page about to be emptied. A clamp keyed to the POST-refresh (smaller)
 * `data` would have no surviving cell to run it from, if the deleted row was
 * the page's last one. Corrects only when the page WOULD be stranded --
 * unlike an unconditional reset to page 0, deleting a row on a page that
 * remains valid leaves the user exactly where they were.
 */
function clampPageIfEmptied(table: DeleteActionProps['table']): void {
  const { pageIndex } = table.getState().pagination;
  const isLastPage = pageIndex === table.getPageCount() - 1;
  const isOnlyRowOnPage = table.getRowModel().rows.length === 1;

  if (isOnlyRowOnPage && isLastPage && pageIndex > 0) {
    table.setPageIndex(pageIndex - 1);
  }
}

export default function DeleteAction({ cell, table }: DeleteActionProps) {
  const { extensionManager } = useSystem();
  const menuCellRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [hasError, setHasError] = useState(false);
  const studyRow = cell.row.original;
  const studyInstanceUid = studyRow.studyInstanceUid;

  const onOpenChange = useCallback(
    (next: boolean) => {
      // Dialog.tsx's own Esc/overlay-click/X routes all call THIS handler, unguarded by
      // confirmIfEnabled/cancelIfEnabled -- so a delete in flight must be guarded here
      // too, or a later failure has no dialog left to report into.
      if (isDeleting) {
        return;
      }
      setOpen(next);
      if (!next) {
        setHasError(false);
      }
    },
    [isDeleting]
  );

  const onConfirm = useCallback(async () => {
    setIsDeleting(true);
    setHasError(false);

    try {
      await deleteStudy(studyInstanceUid);
    } catch (error) {
      console.error(`[sycai] delete failed for ${studyInstanceUid} ${describeError(error)}`);
      setIsDeleting(false);
      setHasError(true);
      return;
    }

    clampPageIfEmptied(table);

    // D2's architecture, reusing the exact mechanism sycai's own
    // applyPacsRootFromCookie (index.tsx) already relies on: rewriting the
    // active data source's configuration (unchanged) forces
    // ExtensionManager to rebuild the instance and broadcast
    // ACTIVE_DATA_SOURCE_CHANGED, which DataSourceWrapper.tsx is already
    // subscribed to and answers by calling useStudyListQuery's refresh().
    const sourceName = extensionManager.activeDataSourceName;
    const configuration = extensionManager.getDataSourceDefinition(sourceName)?.configuration;
    extensionManager.updateDataSourceConfiguration(sourceName, { ...configuration });

    setIsDeleting(false);
    setOpen(false);
  }, [extensionManager, studyInstanceUid, table]);

  return (
    <>
      <StudyList.ActionOverlayCell cell={cell}>
        <StudyList.ActionOverlayCell.Overlay>
          {/* The wrapper ActionCell.tsx puts here too: without it the trigger's click
              bubbles into the row and fires WorkList.tsx's selection/double-click handlers. */}
          <div
            ref={menuCellRef}
            onClick={event => event.stopPropagation()}
          >
            <StudyList.WorkflowMenu
              studyRow={studyRow}
              extraItems={
                // No preventDefault, deliberately. @radix-ui/react-menu's MenuItem closes the
                // menu only when the select is NOT prevented, and WorkflowMenu is controlled,
                // so that close IS setOpen(false). The dialog keeps focus regardless: its
                // trapped FocusScope holds a document focusin listener that pulls focus back,
                // and the menu's own restore is deferred behind a 150ms animate-out anyway.
                <DropdownMenuItem
                  data-cy="sycai-worklist-delete"
                  onSelect={() => setOpen(true)}
                >
                  Delete
                </DropdownMenuItem>
              }
            />
          </div>
        </StudyList.ActionOverlayCell.Overlay>
      </StudyList.ActionOverlayCell>
      {/* A sibling of the menu, never a child of it: ActionOverlayCell renders only its
          own Value/Overlay children and drops the rest, and Radix unmounts
          DropdownMenuContent on select, which would tear a nested dialog down with it. */}
      <DeleteConfirmDialog
        open={open}
        onOpenChange={onOpenChange}
        hasError={hasError}
        isDeleting={isDeleting}
        onConfirm={onConfirm}
        onCloseAutoFocus={event => {
          // @radix-ui/react-dialog's DialogContentModal answers this event by preventing
          // FocusScope's fallback and focusing context.triggerRef -- null here, since no
          // DialogTrigger opens this dialog -- so without us focus is left on nothing and
          // lands on body. The wrapper holds exactly one button, the menu's own trigger.
          event.preventDefault();
          menuCellRef.current?.querySelector('button')?.focus();
        }}
      />
    </>
  );
}
